/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/KeyValuePair.ts
 * @create:      2025-07-01 17:10:56.406
 * @modify:      2025-07-11 21:19:12.937
 * @version:     6.0.0
 * @times:       10
 * @lines:       105
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { IsEqual } from "../string/IsEqual";
import { TStringObject } from "../type/basic";

/**
 * Searches for a key-value pair in a dictionary object by key, with optional case-insensitive matching.
 *
 * @param dic - The dictionary object to search, where keys are strings and values are of type T.
 * @param key - The key to search for in the dictionary.
 * @param ignoreCase - Optional. If true, performs a case-insensitive key comparison. Defaults to false.
 * @returns An object containing the matching key and its value if found; otherwise, `undefined`.
 * 
 * @since 5.0.0
 * @version 2025-06-19
 */
export function FindKeyValuePair<T>(dic: TStringObject<T>, key: string, ignoreCase = false): undefined | { key: string; value: T } {
    // Iterate through each key in the dictionary
    for (const name of Object.keys(dic)) {
        // Check if the current key matches the specified key, considering case sensitivity based on the 'ignoreCase' parameter
        if (IsEqual(name, key, ignoreCase)) {
            // Return the matching key-value pair
            return {
                key: name,
                value: dic[name],
            };
        }
    }

    // Return undefined if no match is found
    return undefined;
}

/**
 * Deletes a key-value pair from the given dictionary object based on the specified key.
 *
 * @param dic - The dictionary object from which to delete the key-value pair.
 * @param key - The key of the key-value pair to delete.
 * @param ignoreCase - Optional. If true, performs a case-insensitive key comparison. Defaults to false.
 * @returns The deleted key-value pair as an object containing the key and value, or `undefined` if the key was not found.
 * 
 * @since 5.0.0
 * @version 2025-06-19
 */
export function DeleteKeyValuePair<T>(dic: TStringObject<T>, key: string, ignoreCase = false): undefined | { key: string; value: T } {
    // Find the key-value pair based on the specified key and value
    const pair = FindKeyValuePair(dic, key, ignoreCase);

    // Check if the key-value pair was found
    if (pair) {
        // Delete the key-value pair from the dictionary
        delete dic[pair.key];

        // Return the deleted key-value pair
        return pair;
    }

    // Return undefined if the key-value pair is not found
    return undefined;
}

/**
 * Removes all key-value pairs from the given dictionary object.
 *
 * @param dict - The dictionary object to clear. All properties will be deleted from this object.
 * 
 * @since 5.0.0
 * @version 2025-06-19
 */
export function ClearKeyValuePair<T>(dict: TStringObject<T>): void {
    for (const key of Object.keys(dict)) {
        delete dict[key];
    }
}
